"use client";

import { useState, useEffect } from "react";

interface CountdownProps {
  targetDate: string;
}

function getTimeLeft(target: string) {
  const diff = new Date(target).getTime() - Date.now();
  if (diff <= 0) return null;

  return {
    days: Math.floor(diff / (1000 * 60 * 60 * 24)),
    hours: Math.floor((diff / (1000 * 60 * 60)) % 24),
    minutes: Math.floor((diff / (1000 * 60)) % 60),
    seconds: Math.floor((diff / 1000) % 60),
  };
}

function CountdownDigit({ value }: { value: string }) {
  return (
    <span className="inline-block font-display font-black text-2xl md:text-4xl text-white tabular-nums">
      {value}
    </span>
  );
}

function CountdownUnit({ value, label }: { value: number; label: string }) {
  const padded = String(value).padStart(2, "0");
  return (
    <div className="flex flex-col items-center min-w-12 md:min-w-18">
      <div className="flex">
        <CountdownDigit value={padded[0]} />
        <CountdownDigit value={padded[1]} />
      </div>
      <span className="text-white/60 text-[10px] md:text-xs uppercase tracking-[0.2em] mt-1.5 font-medium">
        {label}
      </span>
    </div>
  );
}

export function Countdown({ targetDate }: CountdownProps) {
  const [time, setTime] = useState<ReturnType<typeof getTimeLeft>>(null);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    setTime(getTimeLeft(targetDate));
    const interval = setInterval(() => {
      setTime(getTimeLeft(targetDate));
    }, 1000);
    return () => clearInterval(interval);
  }, [targetDate]);

  // SSR / pre-mount: show placeholder to avoid hydration mismatch
  if (!mounted) {
    return (
      <div className="inline-flex gap-4 md:gap-8 justify-center bg-bunting-navy/40 backdrop-blur-sm rounded-2xl px-6 md:px-8 py-5 md:py-6 border border-white/10">
        <div className="h-18 md:h-22" />
      </div>
    );
  }

  // Event has passed
  if (!time) {
    return (
      <div className="bg-bunting-navy/40 backdrop-blur-sm rounded-2xl px-8 py-6 border border-white/10 text-center">
        <p className="font-accent text-accent text-3xl md:text-4xl">The Parade Was a Blast!</p>
        <p className="text-white/70 text-sm mt-2">Thanks for celebrating with us. See you next year!</p>
      </div>
    );
  }

  return (
    <div className="inline-flex gap-4 md:gap-8 justify-center bg-bunting-navy/40 backdrop-blur-sm rounded-2xl px-6 md:px-8 py-5 md:py-6 border border-white/10">
      <CountdownUnit value={time.days} label="Days" />
      <span className="text-white/20 text-xl md:text-3xl font-thin self-start mt-0.5">:</span>
      <CountdownUnit value={time.hours} label="Hours" />
      <span className="text-white/20 text-xl md:text-3xl font-thin self-start mt-0.5">:</span>
      <CountdownUnit value={time.minutes} label="Minutes" />
      <span className="text-white/20 text-xl md:text-3xl font-thin self-start mt-0.5">:</span>
      <CountdownUnit value={time.seconds} label="Seconds" />
    </div>
  );
}
